import { emptySchedule, type ProfileSchedule } from '@/features/scheduler/types';

const KEY = 'scheduler.profiles.v1';

export function uid(): string {
  return `sch-${Date.now().toString(36)}-${Math.random().toString(16).slice(2, 7)}`;
}

export function loadSchedules(): ProfileSchedule[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return JSON.parse(raw) as ProfileSchedule[];
  } catch {
    /* fall through to seed */
  }
  const seeded = seed();
  saveSchedules(seeded);
  return seeded;
}

export function saveSchedules(list: ProfileSchedule[]): void {
  localStorage.setItem(KEY, JSON.stringify(list));
}

const ago = (ms: number) => new Date(Date.now() - ms).toISOString();

function make(overrides: Partial<ProfileSchedule>): ProfileSchedule {
  return { ...emptySchedule(), id: uid(), ...overrides };
}

/** Pre-populated, realistic examples so the page looks alive in the demo. */
function seed(): ProfileSchedule[] {
  return [
    make({
      name: 'Customer Master — Daily Profiling',
      description: 'KYC-critical completeness & uniqueness checks on the customer master.',
      datasetNames: ['Al Wadi - Customers'],
      frequency: 'daily',
      time: '02:00',
      metrics: ['row_count', 'null_rate', 'distinct_count', 'pattern_conformance'],
      priority: 'high',
      alertThreshold: 90,
      notifyEmails: 'data.governance@alwadi.example',
      lastRun: ago(20 * 3600 * 1000),
      lastStatus: 'success',
    }),
    make({
      name: 'Accounts — Weekly Quality Scan',
      description: 'Full-scan validity and consistency profile of the accounts ledger.',
      datasetNames: ['Al Wadi - Accounts'],
      frequency: 'weekly',
      dayOfWeek: 1,
      time: '03:30',
      priority: 'normal',
      retries: 2,
      lastRun: ago(3 * 24 * 3600 * 1000),
      lastStatus: 'success',
    }),
    make({
      name: 'Transactions — Hourly Freshness Check',
      description: 'Lightweight freshness & volume check on the transaction stream.',
      datasetNames: ['Al Wadi - Transactions'],
      frequency: 'hourly',
      metrics: ['row_count', 'freshness_lag'],
      scanMode: 'sample',
      samplePercent: 25,
      priority: 'high',
      timeoutMinutes: 10,
      notifyOnAnomaly: true,
      lastRun: ago(42 * 60 * 1000),
      lastStatus: 'success',
    }),
    make({
      name: 'Full Estate — Monthly Deep Profile',
      description: 'Exhaustive profile across every catalogued dataset for the governance report.',
      datasetNames: ['All catalogued datasets'],
      scope: 'all_columns',
      frequency: 'monthly',
      dayOfMonth: 1,
      time: '01:00',
      metrics: ['row_count', 'null_rate', 'distinct_count', 'pattern_conformance', 'freshness_lag'],
      priority: 'low',
      maxConcurrency: 4,
      timeoutMinutes: 120,
      retries: 3,
      enabled: false,
      lastRun: ago(31 * 24 * 3600 * 1000),
      lastStatus: 'failed',
    }),
  ];
}
